import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import {
  EMPOWERMENT_ZONE_TYPES,
  getEmpowermentZoneCategory,
} from "./empowermentZones";

const ZONE_COLORS = {
  [EMPOWERMENT_ZONE_TYPES.EMPOWERMENT]: "#1a9c4b",
  [EMPOWERMENT_ZONE_TYPES.RURAL]: "#d97706",
  [EMPOWERMENT_ZONE_TYPES.NONE]: "#4c6ef5",
};

export const buildGraphHtml = ({ totalBusinesses, filteredView, charts }) => {
  const chartsJson = JSON.stringify(charts);
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>Credit Graph</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;padding:24px;background:#f8f9fb;color:#23244c}
    .summary{display:flex;justify-content:space-between;margin-bottom:24px;font-size:14px;font-weight:600}
    .grid{display:grid;grid-template-columns:repeat(2,1fr);gap:20px}
    @media(max-width:700px){.grid{grid-template-columns:1fr}}
    .card{background:#fff;border-radius:12px;border:1px solid #e8eaf0;padding:20px;box-shadow:0 2px 8px rgba(0,0,0,0.04)}
    .card-title{font-weight:700;font-size:15px;margin-bottom:16px}
    .chart-area{display:flex;align-items:flex-start;gap:16px}
    .legend{flex:1;min-width:0}
    .legend-item{display:flex;align-items:flex-start;gap:8px;margin-bottom:7px;font-size:12px;line-height:1.4}
    .legend-item>span{flex:1;min-width:0;word-break:break-word;overflow-wrap:break-word}
    .dot{width:10px;height:10px;border-radius:2px;flex-shrink:0;margin-top:2px}
    .val{font-weight:600}
  </style>
</head>
<body>
  <div class="summary">
    <span>Total Businesses: ${totalBusinesses}</span>
    <span>Filtered View: ${filteredView}</span>
  </div>
  <div class="grid" id="grid"></div>
  <script>
    var charts=${chartsJson};
    function r3(n){return Math.round(n*1000)/1000}
    function xy(cx,cy,r,pct){var d=pct*360-90,rad=d*Math.PI/180;return[cx+r*Math.cos(rad),cy+r*Math.sin(rad)]}
    function arc(cx,cy,r,ri,s,e){
      var p1=xy(cx,cy,r,s),p2=xy(cx,cy,r,e),p3=xy(cx,cy,ri,e),p4=xy(cx,cy,ri,s),lg=(e-s)>0.5?1:0;
      return'M'+r3(p1[0])+','+r3(p1[1])+' A'+r+','+r+' 0 '+lg+' 1 '+r3(p2[0])+','+r3(p2[1])+' L'+r3(p3[0])+','+r3(p3[1])+' A'+ri+','+ri+' 0 '+lg+' 0 '+r3(p4[0])+','+r3(p4[1])+'Z'
    }
    function svgChart(chart){
      var cx=70,cy=70,r=55,ri=36,pos=chart.segments.filter(function(s){return s.percent>0}),paths='';
      if(pos.length===0){paths='<circle cx="'+cx+'" cy="'+cy+'" r="'+((r+ri)/2)+'" fill="none" stroke="#e4e7f1" stroke-width="'+(r-ri)+'"/>';}
      else if(pos.length===1){paths='<circle cx="'+cx+'" cy="'+cy+'" r="'+((r+ri)/2)+'" fill="none" stroke="'+pos[0].color+'" stroke-width="'+(r-ri)+'"/>';}
      else{var cum=0;pos.forEach(function(seg){var s=cum/100,e=(cum+seg.percent)/100;paths+='<path d="'+arc(cx,cy,r,ri,s,e)+'" fill="'+seg.color+'"/>';cum+=seg.percent;})}
      var yc=cy+(chart.centerSub?-8:0);
      var ct=chart.centerLabel?'<text x="'+cx+'" y="'+yc+'" text-anchor="middle" dominant-baseline="middle" font-size="11" font-weight="600" fill="#23244c">'+chart.centerLabel+'</text>':'';
      var cs=chart.centerSub?'<text x="'+cx+'" y="'+(cy+12)+'" text-anchor="middle" font-size="9" fill="#888">'+chart.centerSub+'</text>':'';
      return'<svg width="140" height="140" viewBox="0 0 140 140" style="flex-shrink:0">'+paths+ct+cs+'</svg>'
    }
    function legend(items){return items.map(function(i){return'<div class="legend-item"><div class="dot" style="background:'+i.color+'"></div><span>'+i.label+' • <span class="val">'+i.value+'</span> ('+i.percent+')</span></div>'}).join('')}
    var grid=document.getElementById('grid');
    charts.forEach(function(chart){
      var d=document.createElement('div');d.className='card';
      d.innerHTML='<div class="card-title">'+chart.title+'</div><div class="chart-area">'+svgChart(chart)+'<div class="legend">'+legend(chart.legend)+'</div></div>';
      grid.appendChild(d);
    });
  </script>
</body>
</html>`;
};

export const downloadMapAsHtml = ({ businesses, dispatch, showToastFn }) => {
  const validBusinesses = businesses.filter((b) => b.lat && b.lng);
  if (!validBusinesses.length) {
    if (dispatch && showToastFn) {
      dispatch(showToastFn({ message: "No locations available to download.", type: "error" }));
    }
    return;
  }

  const markersData = validBusinesses.map((b) => {
    const category = getEmpowermentZoneCategory(b.empowermentZone);
    const color = ZONE_COLORS[category] || ZONE_COLORS[EMPOWERMENT_ZONE_TYPES.NONE];
    const totalCredit = b.taxCredits?.[0]?.totalProjectedTaxCreditAmount;
    const availableCredits = (b.taxCredits || []).flatMap((tc) => tc.availableTaxCredits || []);
    return {
      lat: parseFloat(b.lat),
      lng: parseFloat(b.lng),
      name: b.businessName || "Business",
      address: [b.physicalAddress, b.physicalCity, b.state, b.physicalZip].filter(Boolean).join(", "),
      totalCredit: totalCredit ? "$" + Number(totalCredit).toLocaleString() : "No data",
      credits: availableCredits.map((c) => c.name).join(", ") || "None",
      color,
    };
  });

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Credit Map</title>
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" crossorigin="" />
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js" crossorigin=""></script>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
    #map { width: 100vw; height: 100vh; }
    .popup-content { min-width: 220px; padding: 2px; }
    .popup-name { font-weight: 700; font-size: 14px; margin-bottom: 8px; color: #23244c; }
    .popup-row { display: flex; gap: 6px; margin-bottom: 5px; font-size: 12px; line-height: 1.4; }
    .popup-label { color: #666; font-weight: 600; min-width: 100px; flex-shrink: 0; }
    .popup-value { color: #333; }
    .legend { position: absolute; bottom: 30px; left: 10px; z-index: 1000; background: white; padding: 10px 14px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.15); font-size: 12px; }
    .legend-title { font-weight: 700; margin-bottom: 8px; color: #23244c; font-size: 13px; }
    .legend-item { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; }
    .legend-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
  </style>
</head>
<body>
  <div id="map"></div>
  <div class="legend">
    <div class="legend-title">Zone Type</div>
    <div class="legend-item"><div class="legend-dot" style="background:#1a9c4b"></div>Federal Empowerment Zones</div>
    <div class="legend-item"><div class="legend-dot" style="background:#d97706"></div>Rural Renewal Counties</div>
    <div class="legend-item"><div class="legend-dot" style="background:#4c6ef5"></div>No Zone Designation</div>
  </div>
  <script>
    var markersData = ${JSON.stringify(markersData)};
    var map = L.map('map');
   L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 19
}).addTo(map);
    var bounds = [];
    markersData.forEach(function(m) {
      var svgPin = '<svg viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg" width="28" height="40"><path d="M12 0C5.373 0 0 5.373 0 12c0 9.45 12 24 12 24S24 21.45 24 12C24 5.373 18.627 0 12 0z" fill="' + m.color + '"/><circle cx="12" cy="12" r="5" fill="rgba(255,255,255,0.9)"/></svg>';
      var icon = L.divIcon({ className: '', html: svgPin, iconSize: [28, 40], iconAnchor: [14, 40], popupAnchor: [0, -42] });
      var popup = '<div class="popup-content">' +
        '<div class="popup-name">' + m.name + '</div>' +
        (m.address ? '<div class="popup-row"><span class="popup-label">Address</span><span class="popup-value">' + m.address + '</span></div>' : '') +
        '<div class="popup-row"><span class="popup-label">Projected Credit</span><span class="popup-value">' + m.totalCredit + '</span></div>' +
        '<div class="popup-row"><span class="popup-label">Credits</span><span class="popup-value">' + m.credits + '</span></div>' +
        '</div>';
      var marker = L.marker([m.lat, m.lng], { icon: icon }).addTo(map);
      marker.bindPopup(popup, { maxWidth: 300 });
      marker.on('mouseover', function() { this.openPopup(); });
      bounds.push([m.lat, m.lng]);
    });
    if (bounds.length === 1) { map.setView(bounds[0], 11); }
    else if (bounds.length > 1) { map.fitBounds(bounds, { padding: [40, 40] }); }
    else { map.setView([37.0902, -95.7129], 4); }
  </script>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  saveAs(blob, "credit-map.html");
};

// ── PDF helpers ───────────────────────────────────────────────────────────────

function canvasRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function canvasWrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const word of words) {
    const test = line ? line + " " + word : word;
    if (ctx.measureText(test).width <= maxWidth) {
      line = test;
    } else {
      if (line) lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [""];
}

function canvasDonut(ctx, cx, cy, r, ri, segments) {
  const active = (segments || []).filter((s) => s && s.percent > 0);
  if (active.length === 0) {
    ctx.beginPath();
    ctx.arc(cx, cy, (r + ri) / 2, 0, Math.PI * 2);
    ctx.strokeStyle = "#e4e7f1";
    ctx.lineWidth = r - ri;
    ctx.stroke();
    ctx.lineWidth = 1;
    return;
  }
  if (active.length === 1) {
    ctx.beginPath();
    ctx.arc(cx, cy, (r + ri) / 2, 0, Math.PI * 2);
    ctx.strokeStyle = active[0].color;
    ctx.lineWidth = r - ri;
    ctx.stroke();
    ctx.lineWidth = 1;
    return;
  }
  let start = -Math.PI / 2;
  active.forEach((seg) => {
    const end = start + (seg.percent / 100) * Math.PI * 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r, start, end);
    ctx.arc(cx, cy, ri, end, start, true);
    ctx.closePath();
    ctx.fillStyle = seg.color;
    ctx.fill();
    start = end;
  });
}

function saveCanvasAsPdf(canvas, W, H, filename) {
  const mmW = (W / 96) * 25.4;
  const mmH = (H / 96) * 25.4;
  const imgData = canvas.toDataURL("image/jpeg", 0.92);
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: [mmW, mmH] });
  pdf.addImage(imgData, "JPEG", 0, 0, mmW, mmH);
  pdf.save(filename);
}

export function renderGraphCanvas({ totalBusinesses, filteredView, charts }) {
  const SCALE = 2;
  const W = 1100, PAD = 24;
  const CARD_PAD = 20, CHART_D = 140, ITEM_H = 18, TITLE_H = 36;
  const CARD_W = (W - PAD * 2 - 20) / 2;
  const LEG_X_OFFSET = CARD_PAD + CHART_D + 16;
  const LEG_MAX_W = CARD_W - LEG_X_OFFSET - CARD_PAD;

  // Pre-compute wrapped lines for each legend item (needs a temp canvas for measurement)
  const measureCanvas = document.createElement("canvas");
  const mCtx = measureCanvas.getContext("2d");
  mCtx.font = "12px Arial, sans-serif";
  const chartWrappedLines = charts.map((chart) =>
    (chart.legend || []).map((item) =>
      canvasWrapText(mCtx, `${item.label}  •  ${item.value}  (${item.percent})`, LEG_MAX_W)
    )
  );
  const chartTotalLines = chartWrappedLines.map((items) =>
    items.reduce((sum, lines) => sum + lines.length, 0)
  );

  const cardHeights = charts.map((c, idx) =>
    CARD_PAD + TITLE_H + Math.max(CHART_D, chartTotalLines[idx] * ITEM_H + 8) + CARD_PAD
  );
  const rows = [];
  for (let i = 0; i < charts.length; i += 2) {
    rows.push(Math.max(cardHeights[i] || 0, cardHeights[i + 1] || 0));
  }
  const SUMMARY_H = 40, ROW_GAP = 20, GRID_TOP = PAD + SUMMARY_H + 16;
  let H = GRID_TOP;
  rows.forEach((h) => { H += h + ROW_GAP; });
  H = Math.max(H + PAD, 400);

  const canvas = document.createElement("canvas");
  canvas.width = W * SCALE;
  canvas.height = H * SCALE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.scale(SCALE, SCALE);

  ctx.fillStyle = "#f8f9fb";
  ctx.fillRect(0, 0, W, H);

  ctx.font = '600 14px "Segoe UI", Arial, sans-serif';
  ctx.fillStyle = "#23244c";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(`Total Businesses: ${totalBusinesses}`, PAD, PAD + 24);
  ctx.textAlign = "right";
  ctx.fillText(`Filtered View: ${filteredView}`, W - PAD, PAD + 24);

  let cardY = GRID_TOP;
  for (let row = 0; row < rows.length; row++) {
    for (let col = 0; col < 2; col++) {
      const idx = row * 2 + col;
      if (idx >= charts.length) continue;
      const chart = charts[idx];
      const cardH = cardHeights[idx];
      const cardX = PAD + col * (CARD_W + 20);

      ctx.fillStyle = "white";
      ctx.strokeStyle = "#e8eaf0";
      ctx.lineWidth = 1;
      canvasRoundRect(ctx, cardX, cardY, CARD_W, cardH, 12);
      ctx.fill();
      ctx.stroke();

      ctx.font = 'bold 15px "Segoe UI", Arial, sans-serif';
      ctx.fillStyle = "#23244c";
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      ctx.fillText(chart.title, cardX + CARD_PAD, cardY + CARD_PAD + 18);

      const cx = cardX + CARD_PAD + 70;
      const cy = cardY + CARD_PAD + TITLE_H + 70;
      canvasDonut(ctx, cx, cy, 55, 36, chart.segments);

      if (chart.centerLabel) {
        const yLabel = cy + (chart.centerSub ? -8 : 0);
        ctx.font = 'bold 11px Arial, sans-serif';
        ctx.fillStyle = "#23244c";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(chart.centerLabel, cx, yLabel);
        if (chart.centerSub) {
          ctx.font = "9px Arial, sans-serif";
          ctx.fillStyle = "#888";
          ctx.fillText(chart.centerSub, cx, cy + 12);
        }
      }

      const legX = cardX + CARD_PAD + CHART_D + 16;
      let legY = cardY + CARD_PAD + TITLE_H + 8;
      const wrappedItems = chartWrappedLines[idx] || [];
      chart.legend.forEach((item, li) => {
        const lines = wrappedItems[li] || [`${item.label}  •  ${item.value}  (${item.percent})`];
        canvasRoundRect(ctx, legX, legY + 2, 10, 10, 2);
        ctx.fillStyle = item.color;
        ctx.fill();
        ctx.font = "12px Arial, sans-serif";
        ctx.fillStyle = "#23244c";
        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";
        lines.forEach((line, lineIdx) => {
          ctx.fillText(line, legX + 14, legY + 11 + lineIdx * ITEM_H);
        });
        legY += lines.length * ITEM_H;
      });
    }
    cardY += rows[row] + ROW_GAP;
  }

  return { canvas, W, H };
}

export const downloadGraphAsPdf = (graphData) => {
  const result = renderGraphCanvas(graphData);
  if (!result) return;
  saveCanvasAsPdf(result.canvas, result.W, result.H, "credit-graph.pdf");
};

// ── Tile helpers (Web Mercator / CartoDB) ─────────────────────────────────────

// Convert lng/lat to global pixel coordinates at a given zoom level (1 tile = 256px)
function lngToPx(lng, z) { return (lng + 180) / 360 * 256 * (1 << z); }
function latToPy(lat, z) {
  const r = lat * Math.PI / 180;
  return (1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2 * 256 * (1 << z);
}

function loadImgSrc(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Draws the standard PDF header bar onto a canvas result returned by renderMapCanvas / renderGraphCanvas.
// Returns a new { canvas, W, H } with the header prepended at the top.
export async function composeWithHeader(result, { userName, logoUrl, date }) {
  const HEADER_H = 100;
  const { canvas: src, W, H } = result;

  const out = document.createElement("canvas");
  out.width = W;
  out.height = H + HEADER_H;
  const ctx = out.getContext("2d");

  // Header background
  ctx.fillStyle = "#444549";
  ctx.fillRect(0, 0, W, HEADER_H);

  // Left: title + subtitle
  ctx.fillStyle = "#ffffff";
  ctx.font = 'bold 23px "Segoe UI", Arial, sans-serif';
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("Tax Credit Analysis", 32, 42);
  ctx.font = '14px "Segoe UI", Arial, sans-serif';
  ctx.fillText(`Provided by: ${userName}`, 32, 68);

  // Right: POWERED BY + logo + date
  ctx.font = 'bold 15px "Segoe UI", Arial, sans-serif';
  ctx.textAlign = "right";
  ctx.fillText("POWERED BY:", W - 32, 28);

  if (logoUrl) {
    try {
      const logoImg = await loadImgSrc(logoUrl);
      const logoH = 28;
      const logoW = logoImg.naturalWidth
        ? logoImg.naturalWidth * (logoH / logoImg.naturalHeight)
        : 90;
      ctx.drawImage(logoImg, W - 32 - logoW, 34, logoW, logoH);
    } catch (_) {}
  }

  ctx.font = '14px "Segoe UI", Arial, sans-serif';
  ctx.fillStyle = "#ffffff";
  ctx.fillText(date, W - 32, 88);

  // Draw original canvas content below the header (scaled to W×H in case src is 2×)
  ctx.drawImage(src, 0, HEADER_H, W, H);

  return { canvas: out, W, H: H + HEADER_H };
}

async function loadTile(z, x, y) {
  const sub = "abcd"[(x + y) % 4];
  const url = `https://${sub}.basemaps.cartocdn.com/rastertiles/voyager/${z}/${x}/${y}.png`;
  const blob = await fetch(url).then((r) => r.blob());
  const objUrl = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(objUrl); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(objUrl); reject(); };
    img.src = objUrl;
  });
}

export async function renderMapCanvas(businesses) {
  const validBusinesses = businesses.filter((b) => b.lat && b.lng);
  if (!validBusinesses.length) return null;

  const markersData = validBusinesses.map((b) => {
    const category = getEmpowermentZoneCategory(b.empowermentZone);
    const color = ZONE_COLORS[category] || ZONE_COLORS[EMPOWERMENT_ZONE_TYPES.NONE];
    return { lat: parseFloat(b.lat), lng: parseFloat(b.lng), color };
  });

  const lats = markersData.map((m) => m.lat);
  const lngs = markersData.map((m) => m.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;

  const W = 1100, H = 680, TILE_SIZE = 256, MARKER_PAD = 80;

  let zoom = 2;
  for (let z = 14; z >= 2; z--) {
    const bboxW = lngToPx(maxLng, z) - lngToPx(minLng, z);
    const bboxH = latToPy(minLat, z) - latToPy(maxLat, z);
    if (bboxW <= W - MARKER_PAD * 2 && bboxH <= H - MARKER_PAD * 2) { zoom = z; break; }
  }

  const cx = lngToPx(centerLng, zoom);
  const cy = latToPy(centerLat, zoom);
  const worldPx = TILE_SIZE * (1 << zoom);
  const originX = Math.max(0, Math.min(worldPx - W, cx - W / 2));
  const originY = Math.max(0, Math.min(worldPx - H, cy - H / 2));

  const tx1 = Math.floor(originX / TILE_SIZE);
  const tx2 = Math.floor((originX + W - 1) / TILE_SIZE);
  const ty1 = Math.floor(originY / TILE_SIZE);
  const ty2 = Math.floor((originY + H - 1) / TILE_SIZE);
  const maxTileIdx = (1 << zoom) - 1;

  const toX = (lng) => lngToPx(lng, zoom) - originX;
  const toY = (lat) => latToPy(lat, zoom) - originY;

  const jobs = [];
  for (let tx = Math.max(0, tx1); tx <= Math.min(maxTileIdx, tx2); tx++)
    for (let ty = Math.max(0, ty1); ty <= Math.min(maxTileIdx, ty2); ty++)
      jobs.push(loadTile(zoom, tx, ty).then((img) => ({ img, tx, ty })).catch(() => null));

  const tiles = await Promise.all(jobs);

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#c9e8f5";
  ctx.fillRect(0, 0, W, H);

  tiles.forEach((t) => {
    if (!t) return;
    const px = t.tx * TILE_SIZE - originX;
    const py = t.ty * TILE_SIZE - originY;
    ctx.drawImage(t.img, px, py, TILE_SIZE, TILE_SIZE);
  });

  const PIN_D = "M12 0C5.373 0 0 5.373 0 12c0 9.45 12 24 12 24S24 21.45 24 12C24 5.373 18.627 0 12 0z";
  markersData.forEach((m) => {
    const x = toX(m.lng), y = toY(m.lat);
    ctx.save();
    ctx.translate(x - 12, y - 36);
    ctx.fillStyle = m.color;
    ctx.fill(new Path2D(PIN_D));
    ctx.beginPath();
    ctx.arc(12, 12, 5, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fill();
    ctx.restore();
  });

  const legX = 20, legY = H - 118;
  ctx.fillStyle = "white";
  ctx.strokeStyle = "#e0e8f0";
  ctx.lineWidth = 1.5;
  canvasRoundRect(ctx, legX, legY, 228, 100, 6);
  ctx.fill();
  ctx.stroke();
  ctx.font = "bold 13px Arial, sans-serif";
  ctx.fillStyle = "#23244c";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("Zone Type", legX + 10, legY + 20);
  const LEG_ITEMS = [
    { color: "#1a9c4b", label: "Federal Empowerment Zones" },
    { color: "#d97706", label: "Rural Renewal Counties" },
    { color: "#4c6ef5", label: "No Zone Designation" },
  ];
  LEG_ITEMS.forEach((item, i) => {
    const iy = legY + 38 + i * 22;
    ctx.beginPath();
    ctx.arc(legX + 18, iy, 6, 0, Math.PI * 2);
    ctx.fillStyle = item.color;
    ctx.fill();
    ctx.font = "11px Arial, sans-serif";
    ctx.fillStyle = "#333";
    ctx.textBaseline = "middle";
    ctx.fillText(item.label, legX + 32, iy);
  });

  return { canvas, W, H };
}

export const downloadMapAsPdf = async ({ businesses, dispatch, showToastFn }) => {
  const hasCoords = businesses.some((b) => b.lat && b.lng);
  if (!hasCoords) {
    if (dispatch && showToastFn) {
      dispatch(showToastFn({ message: "No locations available to download.", type: "error" }));
    }
    return;
  }
  try {
    const result = await renderMapCanvas(businesses);
    if (result) saveCanvasAsPdf(result.canvas, result.W, result.H, "credit-map.pdf");
  } catch (err) {
    console.error("Map PDF error:", err);
    if (dispatch && showToastFn) {
      dispatch(showToastFn({ message: "Failed to generate map PDF. Please try again.", type: "error" }));
    }
  }
};

// detailsElement: optional DOM element whose direct children are rendered as additional PDF pages.
export const downloadFullAnalysisPdf = async ({ businesses, graphData, detailsElement, dispatch, showToastFn }) => {
  const toMm = (px) => (px / 96) * 25.4;

  try {
    const [mapResult, graphResult] = await Promise.all([
      renderMapCanvas(businesses),
      Promise.resolve(renderGraphCanvas(graphData)),
    ]);

    if (!graphResult) {
      if (dispatch && showToastFn) {
        dispatch(showToastFn({ message: "Failed to generate PDF.", type: "error" }));
      }
      return;
    }

    // Capture each child of detailsElement as a separate page via html2canvas
    const detailPages = [];
    if (detailsElement) {
      const sections = Array.from(detailsElement.children);
      for (const section of sections) {
        try {
          const sW = section.offsetWidth || 900;
          const sH = section.scrollHeight || section.offsetHeight || 1000;
          const canvas = await html2canvas(section, {
            scale: 1.5,
            useCORS: true,
            backgroundColor: "#ffffff",
            logging: false,
            width: sW,
            height: sH,
          });
          detailPages.push({ canvas, W: sW, H: sH });
        } catch (e) {
          console.error("Failed to capture business section:", e);
        }
      }
    }

    // Build ordered page list: map → graph → business details
    const allPages = [];
    if (mapResult) allPages.push(mapResult);
    allPages.push(graphResult);
    detailPages.forEach((p) => allPages.push(p));

    const [first, ...rest] = allPages;
    const pdf = new jsPDF({ unit: "mm", format: [toMm(first.W), toMm(first.H)] });
    pdf.addImage(first.canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, toMm(first.W), toMm(first.H));

    rest.forEach((page) => {
      pdf.addPage([toMm(page.W), toMm(page.H)]);
      pdf.addImage(page.canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, toMm(page.W), toMm(page.H));
    });

    pdf.save("credit-analysis.pdf");
  } catch (err) {
    console.error("Full analysis PDF error:", err);
    if (dispatch && showToastFn) {
      dispatch(showToastFn({ message: "Failed to generate PDF. Please try again.", type: "error" }));
    }
  }
};
